import { useEffect } from 'react';

/**
 * Custom hook to dynamically update document title, meta description, canonical link,
 * robots indexing, Open Graph tags, Twitter tags, and JSON-LD schema markup.
 * 
 * @param {Object} metadata
 * @param {string} metadata.title - The page title
 * @param {string} metadata.description - The page meta description
 * @param {string} [metadata.image] - Optional absolute or relative image URL for social sharing
 * @param {string} [metadata.type] - Optional Open Graph type (defaults to 'website')
 * @param {boolean} [metadata.noindex] - Optional flag to prevent search engine indexing
 */
export default function useDocumentMetadata({ title, description, image, type = 'website', noindex = false }) {
  useEffect(() => {
    const liveDomain = 'https://mediccare.dk';
    const currentPath = window.location.pathname;
    const canonicalUrl = `${liveDomain}${currentPath === '/' ? '/' : currentPath}`;

    // Document Title
    if (title) {
      document.title = title;
      
      // OG Title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', title);

      // Twitter Title
      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.setAttribute('name', 'twitter:title');
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.setAttribute('content', title);
    }
    
    // Meta Description
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
      
      // OG Description
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', description);

      // Twitter Description
      let twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (!twitterDescription) {
        twitterDescription = document.createElement('meta');
        twitterDescription.setAttribute('name', 'twitter:description');
        document.head.appendChild(twitterDescription);
      }
      twitterDescription.setAttribute('content', description);
    }

    // Canonical Link Tag
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Robots Tag
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');
    
    // OG URL
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', canonicalUrl);
    
    // OG Type
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', type);

    // OG Site Name & Locale
    let ogSiteName = document.querySelector('meta[property="og:site_name"]');
    if (!ogSiteName) {
      ogSiteName = document.createElement('meta');
      ogSiteName.setAttribute('property', 'og:site_name');
      document.head.appendChild(ogSiteName);
    }
    ogSiteName.setAttribute('content', 'MedicCare');

    let ogLocale = document.querySelector('meta[property="og:locale"]');
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      ogLocale.setAttribute('property', 'og:locale');
      document.head.appendChild(ogLocale);
    }
    ogLocale.setAttribute('content', 'da_DK');

    // Twitter Card
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', 'summary_large_image');
    
    // OG & Twitter Image
    const defaultImg = 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mediccare-v3-mork.jpg';
    const absoluteImageUrl = image
      ? (image.startsWith('http') ? image : `${liveDomain}${image}`)
      : defaultImg;

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', absoluteImageUrl);

    let twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      document.head.appendChild(twitterImage);
    }
    twitterImage.setAttribute('content', absoluteImageUrl);

    // Inject / Update JSON-LD Schema
    let schemaScript = document.getElementById('schema-mediccare-ld');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('id', 'schema-mediccare-ld');
      schemaScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(schemaScript);
    }

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${liveDomain}/#organization`,
          "name": "MedicCare A/S",
          "url": liveDomain,
          "logo": {
            "@type": "ImageObject",
            "url": defaultImg
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+45-40-83-08-30",
            "contactType": "customer service",
            "email": "info@mediccare.dk",
            "areaServed": "DK",
            "availableLanguage": "Danish"
          }
        },
        {
          "@type": "WebSite",
          "@id": `${liveDomain}/#website`,
          "url": liveDomain,
          "name": "MedicCare",
          "description": "MedicCare leverer digitale medicinhåndteringsløsninger og velfærdsteknologi til de danske kommuner.",
          "publisher": {
            "@id": `${liveDomain}/#organization`
          },
          "inLanguage": "da-DK"
        },
        {
          "@type": "WebPage",
          "@id": `${canonicalUrl}/#webpage`,
          "url": canonicalUrl,
          "name": title || "MedicCare",
          "isPartOf": {
            "@id": `${liveDomain}/#website`
          },
          "description": description || "",
          "inLanguage": "da-DK"
        }
      ]
    };

    schemaScript.textContent = JSON.stringify(schemaData);

  }, [title, description, image, type, noindex]);
}
